import { useAppSelector } from "@app/hooks";
import CategoryPin from "@components/CategoryPin";
import { EmeraldIcon } from "@emerald-react/icon";
import {
  selectPOICategories,
  selectVisiblePOICountByCategoryId,
} from "@features/survey/poisSelectors";
import { selectIsIncludePOI } from "@features/survey/surveySelectors";
import { isPOIKey } from "@features/survey/utils/poiUtils";
import { MapKey as MapKeyType } from "@survey-types/survey.mapkeys.types";
import { memo, useMemo } from "react";
import { useLocation } from "react-router";

const MapKey = memo(function MapKey() {
  const mapKeys = useAppSelector((state) => state.survey.marketMapKeys);
  const isIncludePOI = useAppSelector(selectIsIncludePOI);
  const poiCategories = useAppSelector(selectPOICategories);
  const visiblePOICountByCategoryId = useAppSelector(
    selectVisiblePOICountByCategoryId,
  );

  const categoryMap = useMemo(
    () => new Map(poiCategories.map((cat) => [cat.extId, cat])),
    [poiCategories],
  );

  const categoryNameMap = useMemo(
    () =>
      new Map(
        poiCategories.map((cat) => [
          cat.categoryName.trim().toLowerCase(),
          cat,
        ]),
      ),
    [poiCategories],
  );

  // Filter map keys the same way for all categories (POI, Property, LeaseComp).
  // POI keys additionally respect the global isIncludePOI toggle.
  const enabledMapKeys = useMemo(() => {
    const resolveCategoryForKey = (key: MapKeyType) => {
      if (!isPOIKey(key, categoryNameMap)) return undefined;

      const linkedId = key.referenceId;
      if (linkedId) {
        return categoryMap.get(linkedId);
      }

      return categoryNameMap.get(key.label?.trim().toLowerCase());
    };

    const baseKeys = mapKeys.filter((key) => {
      const poiKey = isPOIKey(key, categoryNameMap);

      if (poiKey && !isIncludePOI) {
        return false;
      }

      // Hide POI categories that currently have no visible POI rows.
      if (poiKey) {
        const category = resolveCategoryForKey(key);
        const categoryId = category?.extId;
        if (
          !categoryId ||
          (visiblePOICountByCategoryId[categoryId] || 0) === 0
        ) {
          return false;
        }
      }

      return key.active && !key?.isShortlisted;
    });

    if (!isIncludePOI) {
      return baseKeys.slice(0, 8);
    }

    // Track POI categories that already have persisted mapkey rows (active or inactive)
    // so fallback does not re-add keys the user intentionally hid in edit panel.
    const persistedPOICategoryIds = new Set(
      mapKeys
        .filter((key) => isPOIKey(key, categoryNameMap) && !key?.isShortlisted)
        .map((key) => {
          if (key.referenceId) return key.referenceId;
          // Fallback: resolve category from label (legacy keys without ID fields)
          const category = resolveCategoryForKey(key as MapKeyType);
          return category?.extId;
        })
        .filter(Boolean),
    );

    const fallbackPOIKeys: MapKeyType[] = poiCategories
      .filter((cat) => (visiblePOICountByCategoryId[cat.extId] || 0) > 0)
      .filter((cat) => !persistedPOICategoryIds.has(cat.extId))
      .map((cat, index) => {
        return {
          mapKeyId: `poi-fallback-${cat.extId}`,
          label: cat.categoryName,
          color: cat.iconData?.color || "#a23f97",
          active: true,
          isCurrentLocation: false,
          isShortlisted: false,
          order: (baseKeys.length || 0) + index,
          category: "POI",
          referenceId: cat.extId,
        };
      });

    return [...baseKeys, ...fallbackPOIKeys].slice(0, 8);
  }, [
    mapKeys,
    isIncludePOI,
    categoryNameMap,
    poiCategories,
    visiblePOICountByCategoryId,
  ]);

  const location = useLocation();
  const isRenderedView = useMemo(
    () => location.pathname.includes("/preview/renderedMarketMap"),
    [location.pathname],
  );

  return (
    <div
      className={`map-key-section ${isRenderedView ? "rendered-view" : ""}`}
      id="map-key-section"
    >
      <h3 className={`map-key-title ${isRenderedView ? "rendered-view" : ""}`}>
        Map Key
      </h3>
      <ul>
        {enabledMapKeys?.map((key) => {
          const poiKey = isPOIKey(key, categoryNameMap);

          let poiCategory = undefined;
          if (poiKey) {
            const linkedId = key.referenceId;
            if (linkedId) {
              poiCategory = categoryMap.get(linkedId);
            }
            if (!poiCategory && key.label) {
              // Fallback: try to match by label if ID fields are missing (legacy keys)
              poiCategory = categoryNameMap.get(
                key.label?.trim().toLowerCase(),
              );
            }
          }

          const pinType = poiCategory?.pinType || "ICON";
          const iconName = poiCategory?.iconData?.iconName;
          const iconColor = poiCategory?.iconData?.color || key.color;
          const activeLogo = poiCategory?.logoData?.logoLinks?.find(
            (l) => l.isActive !== false,
          );
          const logoUrl = activeLogo?.url;

          return (
            <li key={key.mapKeyId} className="map-key-item">
              <div
                className={`marker-pin ${poiKey ? "marker-pin-poi" : ""}`}
                style={{
                  color: key.color,
                }}
              >
                {poiKey ? (
                  <CategoryPin
                    pinType={pinType}
                    iconColor={iconColor}
                    iconName={iconName}
                    logoUrl={logoUrl}
                    size={20}
                    showStroke={true}
                    className="block"
                    alt={key.label}
                    isRenderedView={isRenderedView}
                  />
                ) : key.category === "LeaseComp" ? (
                  <EmeraldIcon icon="square" type="filled" variant="icon" />
                ) : key.isCurrentLocation ? (
                  <EmeraldIcon icon="stars" type="filled" variant="icon" />
                ) : (
                  <EmeraldIcon
                    style={{ color: key.color }}
                    icon="circle"
                    type="filled"
                    variant="icon"
                  />
                )}
              </div>
              <span className="map-key-label">{key.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
});
export default MapKey;
